import { Theme } from "model/Theme";
import { Stage } from "./Stage";
import { getTimeMs } from "TimeHelper";
import { Links } from "model/Links";

export const TOUR_POINTS_TYPES = ["Driver", "Team"] as const;
export type TourPointsType = (typeof TOUR_POINTS_TYPES)[number];
export const isTourPointsType = (
  value: string | undefined
): value is TourPointsType => {
  return !!value && (TOUR_POINTS_TYPES as readonly string[]).includes(value);
};

export type Tour = {
  id: string;
  description: string | undefined;
  endTimeMs: number;
  filterName: string | undefined;
  // todo - rename? boolean for both grouping tournaments in list to the tour, and for displaying tour information on tournament/match items
  groupTournaments: boolean | undefined;
  imageIdOverride: string | undefined;
  links: Links | undefined;
  name: string;
  points: TourPoints;
  shortName: string;
  stages: Stage[];
  startTimeMs: number;
  theme: Theme | undefined;
  trigram: string;
};

export type TourPoints = {
  config: TourPointsConfig[] | undefined;
  ignoreTournamentIds: string[] | undefined;
  overrides: TourPointsOverrides | undefined;
  type: "Driver" | "Team";
};

export type TourPointsConfig = {
  tournamentIds: string[];
  distribution: number[];
};

export type TourPointsOverrides = {
  [tournamentId: string]:
    | {
        [rosterId: string]: number | "Qual" | undefined;
      }
    | undefined;
};

export const getTourTournamentIds = (tour: Tour) => {
  return tour.stages.flatMap((s) => s.tournamentIds);
};

export const tourContainsTournament = (tour: Tour, tournamentId: string) => {
  return getTourTournamentIds(tour).includes(tournamentId);
};

export const getTourShortTitle = (tour: Tour, tournamentId: string) => {
  if (!tourContainsTournament(tour, tournamentId)) {
    return undefined;
  }

  for (var i = 0; i < tour.stages.length; i++) {
    const stage = tour.stages[i];
    if (stage.tournamentIds.includes(tournamentId) && !!stage.shortName) {
      return `${tour.shortName} ${stage.shortName}`;
    }
  }

  return tour.shortName;
};

export const getTourTitle = (tour: Tour, tournamentId: string) => {
  if (!tourContainsTournament(tour, tournamentId)) {
    return undefined;
  }

  for (var i = 0; i < tour.stages.length; i++) {
    const stage = tour.stages[i];
    if (stage.tournamentIds.includes(tournamentId) && !!stage.name) {
      return `${tour.name} ${stage.name}`;
    }
  }

  return tour.name;
};

// todo - extract status
export const getTourStatus = (tour: Tour) => {
  const time = getTimeMs();
  if (tour.startTimeMs > time) {
    return "Upcoming";
  }

  if (tour.endTimeMs < time) {
    return "Completed";
  }

  return "Ongoing";
};

export const getTourLeaderboardTournamentIds = (tour: Tour) => {
  return getTourTournamentIds(tour).filter(
    (id) => !ignoreTournamentIdForTourPoints(tour, id)
  );
};

const ignoreTournamentIdForTourPoints = (tour: Tour, tournamentId: string) =>
  tour.points.ignoreTournamentIds?.includes(tournamentId) ?? false;
